<template>
    <div v-bind="$attrs" class="date-picker" :style="{display: inline ? 'inline-block' : 'block'}" :class="[heightClass, sizeClass, 'date-picker-type-' + type]" @focusout="focusout">
        <app-popover class="inputAddon" :close-on-scroll="false" :handle-focusable="false" :focus-trap="false" :auto-focus-first-element="false" ref="popover" position="bottom-left">
            <template #default>
                <input
                    type="text"
                    autocomplete="off"
                    ref="input"
                    v-model="formattedDate"
                    v-bind="{...$attrs, ...ariaValidationAttributes}"
                    :hidden="hidden"
                    :name="name"
                    :id="_id"
                    :required="required"
                    :data-selected-date="selectedDate"
                    :class="[{invalid: isInvalid}, 'long', heightClass]"
                    :placeholder="placeholder"
                    @click="open"
                    @blur="focusout"
                    @paste="onPaste"
                    @keydown.enter="parseCurrentInput"
                    v-on="events"
                    :disabled="disabled"
                    :readonly="readonly"
                    aria-label="Eingabe Datum"/>
                <!-- clear button -->
                <span ref="inputClear" v-if="!hideClear && selectedDate" v-interact="clear" :class="{'cursor-not-allowed': noHandle}" class="input-icon icon-interact">
                    <i class="material-icons" aria-hidden="true" role="img">clear</i>
                </span>
                <span v-else-if="!hidden" class="input-icon" :class="{'cursor-not-allowed': noHandle}" @click="open">
                    <i class="material-icons md-18" aria-hidden="true" role="img">{{icon}}</i>
                </span>
            </template>
            <template #container="{}">
                <div class="date-picker-container" :class="{flex: type === 'datetime'}" tabindex="0">
                    <div v-if="showDatepicker" class="date-selection">
                        <div class="date-picker-head">
                            <!-- previous month -->
                            <span class="date-picker-previous" :class="{active: showPreviousMonth}" @click="previousMonth">
                                <i class="material-icons md-12 pointer" aria-hidden="true" role="img">keyboard_arrow_left</i><i class="material-icons md-12 pointer"
                                aria-hidden="true" role="img">keyboard_arrow_left</i>
                            </span>

                            <!-- month/year select -->
                            <span>
                                <form-select :options="availableMonths" @input="focusInput" v-model="selectedMonth" class="kool-choiz-small no-border no-color"
                                    aria-label="Auswahl Monat" disable-search/>
                                <form-select :options="availableYears" @input="focusInput" v-model="selectedYear" class="kool-choiz-small no-border no-color"
                                    aria-label="Auswahl Jahr" disable-search/>
                            </span>

                            <!-- next month -->
                            <span class="date-picker-next" :class="{active: showNextMonth}" @click="nextMonth">
                                <i class="material-icons md-12 pointer" aria-hidden="true" role="img">keyboard_arrow_right</i><i class="material-icons md-12 pointer"
                                aria-hidden="true" role="img">keyboard_arrow_right</i>
                            </span>
                        </div>
                        <ul>
                            <li class="date-picker-weekdays">
                                <span><abbr title="Kalenderwoche">KW</abbr></span>
                                <span v-for="day in weekDays"><abbr :title="day.name">{{day.name[0]}}<template v-if="viewSize.mdUp">{{day.name[1]}}</template></abbr></span>
                            </li>

                            <li v-for="days in availableDays"
                                :class="{'date-picker-week-active': isWeekPicker && days[0].active, 'date-picker-week-selected': isWeekPicker && days[0].selected}">
                                <template v-for="dayInfo in days">
                                    <span v-if="dayInfo.firstDayOfWeek" class="calendar-week" @click="changeDay(isWeekPicker ? days[0] : dayInfo)" @touchstart="changeDay(isWeekPicker ? days[0] : dayInfo)">
                                            {{dayInfo.week}}
                                    </span>
                                    <span
                                        :class="{'date-picker-other-month': !dayInfo.sameMonth, 'date-picker-restricted': dayInfo.restricted, 'date-picker-weekend': dayInfo.weekend, 'date-picker-active': !isWeekPicker && dayInfo.active, 'date-picker-selected': dayInfo.selected, 'date-picker-holiday': dayInfo.holiday}"
                                        v-tooltip="dayInfo.label"
                                        @click="changeDay(isWeekPicker ? days[0] : dayInfo)" @touchstart="changeDay(isWeekPicker ? days[0] : dayInfo)">{{dayInfo.day}}</span>
                                </template>
                            </li>
                        </ul>
                        <hr>
                        <div class="date-picker-footer">
                            <div class="pointer text-center" @click="selectToday"><i class="material-icons md-18" aria-hidden="true" role="img">today</i> {{
                                    isWeekPicker
                                        ? 'Diese Woche'
                                        : 'Heute'
                                }}
                            </div>
                        </div>
                    </div>

                    <div v-if="showTimepicker" class="time-picker">
                        <span class="date-picker-header">Stunde</span>
                        <ul>
                            <li v-for="i in availableHours">
                                <span v-for="j in i" @click="selectHour(j)"
                                    :class="{'date-picker-restricted': j.restricted, 'date-picker-selected': selectedHour === j.hour}">{{('0' + j.hour).slice(-2)}}</span>
                            </li>
                        </ul>
                        <hr>
                        <span class="date-picker-header">Minute</span>
                        <ul class="date-picker-minute">
                            <li v-for="row in availableMinutes">
                                <span v-for="step in row" :class="{'date-picker-restricted': step.restricted, 'date-picker-selected': selectedMinute === step.value}"
                                    @click="selectMinute(step)">{{step.minute}}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </template>
        </app-popover>
    </div>
</template>

<script lang="ts">
    import {incrementDate, monthNames, setToMonday, weekDays} from '@/library/date';
    import ADatePicker from '@/library/form/ADatePicker';
    import {defineAsyncComponentWrapper} from '@/library/utils';
    import {AbstractFormInput} from '@/mixins/AbstractFormInput';
    import {Component, mixins, Prop, Ref, Watch} from 'vue-facing-decorator';
    import dateHelper, {getDateFormat, parseDate} from '../../utils/portuna/dateHelper';
    import FormSelect from './FormSelect.vue';

    const AppPopover = defineAsyncComponentWrapper(() => import('@/components/AppPopover.vue'));

    type AvailableMonth = {
        label: string
        value: number
        disabled: boolean
    };

    type AvailableDay = {
        active: boolean;
        selected: boolean;
        weekend: boolean;
        sameMonth: boolean;
        restricted: boolean;
        day: number;
        month: number;
        year: number;
        label: string;
        firstDayOfWeek: boolean;
        week: string;
        holiday: boolean;
    };

    type AvailableHour = {
        hour: number
        restricted: boolean
    };

    type AvailableMinute = {
        value: number
        minute: string
        restricted: boolean
    };

    @Component({
        name: 'FormInputDate',
        components: {
            FormSelect,
            AppPopover,
        },
        emits: ['blur', 'change', 'update:modelValue'],
    })
    export default class FormInputDate extends mixins(ADatePicker, AbstractFormInput)
    {
        @Ref('popover') readonly popover!: AppPopover;

        readonly $refs: { input: HTMLInputElement, inputClear: HTMLSpanElement };

        @Prop({
            type: String,
            default: 'date',
            required: false,
            validator(value)
            {
                return ['date', 'datetime', 'time', 'week'].includes(value);
            },
        }) type: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) hideClear: boolean;

        @Prop({
            type: String,
            default: null,
            required: false,
        }) min: Nullable<string>;

        @Prop({
            type: String,
            default: null,
            required: false,
        }) max: Nullable<string>;

        @Prop({
            type: String,
        }) modelValue: string;

        @Prop({
            type: String,
        }) overwritePlaceholder: string;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) inline: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) defaultNow: boolean;

        @Prop(({
            type: Number,
            default: 5,
            required: false,
        })) minutesSteps: number;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) focus: boolean;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) disabled: boolean;

        @Prop({
            type: Boolean,
            default: false,
        }) readonly: boolean;

        @Prop({
            type: Object,
            default()
            {
                return {};
            },
        }) events: object;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) hidden: boolean;

        @Prop({
            type: String,
            default: 'long',
            required: false,
            validator: function(value)
            {
                return ['none', 'long', 'medium', 'small'].includes(value);
            },
        }) size: string;

        @Prop({
            type: String,
            default: 'none',
            required: false,
            validator: function(value)
            {
                return ['none', 'flat'].includes(value);
            },
        }) height: string;

        @Prop({
            type: Object,
            default()
            {
                return {};
            },
        }) blockedDays: Record<string, string>;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) closeOnInput: boolean;

        weekDays = weekDays;

        showPreviousMonth: boolean = true;

        showNextMonth: boolean = true;

        initialized = false;

        valueChanged = false;

        ignoreWatcher = false;

        @Watch('type')
        watchType(): void
        {
            this.updateTime();
        }

        @Watch('modelValue')
        watchValue(): void
        {
            if (this.ignoreWatcher)
            {
                return;
            }

            if (!this.modelValue && this.type === 'time')
            {
                this.updateSelectedDate(null);

                return;
            }

            if (!this.modelValue)
            {
                return;
            }

            this.updateSelectedDate(this.setDefaultDate());
        }

        get icon()
        {
            return this.type === 'time' ? 'schedule' : 'calendar_today';
        }

        get placeholder()
        {
            if (this.overwritePlaceholder)
            {
                return this.overwritePlaceholder;
            }

            if (this.type === 'time')
            {
                return 'HH:MM';
            }
            else if (this.type === 'datetime')
            {
                return 'DD.MM.JJJJ HH:MM';
            }

            return 'DD.MM.JJJJ';
        }

        get sizeClass()
        {
            return !this.inline && 'none' !== this.size && this.size || '';
        }

        get heightClass()
        {
            return 'none' !== this.height && this.height || '';
        }

        get showDatepicker()
        {
            return this.type === 'datetime' || this.type === 'date' || this.type === 'week';
        }

        get showTimepicker()
        {
            return this.type === 'time' || this.type === 'datetime';
        }

        get isWeekPicker()
        {
            return this.type === 'week';
        }

        set formattedDate(newValue: string)
        {
            this.valueChanged = true;
        }

        get formattedDate()
        {
            if (this.isWeekPicker)
            {
                if (this.selectedDate)
                {
                    let monday = new Date(this.selectedDate);
                    let sunday = incrementDate(new Date(monday), 6);
                    return (dateHelper('d.m.', monday) + ' - ' + dateHelper('d.m.Y', sunday)) as string;
                }

                return '';
            }

            let format = getDateFormat('d.m.Y', this.type);

            return (this.selectedDate && dateHelper(format, 'time' === this.type ? dateHelper('Y-m-d') + ' ' + this.selectedDate : this.selectedDate)) as string;
        }

        get maxDate()
        {
            let date = this.max ? (parseDate(this.max) as Date || null) : null;
            if (this.type === 'date' && date)
            {
                date.setHours(3);
                date.setMinutes(0);
                date.setSeconds(0);
                date.setMilliseconds(0);
            }

            return date;
        }

        get minDate()
        {
            let date = this.min ? (parseDate(this.min) as Date || null) : null;
            if (this.type === 'date' && date)
            {
                date.setHours(3);
                date.setMinutes(0);
                date.setSeconds(0);
                date.setMilliseconds(0);
            }

            return date;
        }

        get availableMonths(): AvailableMonth[]
        {
            let minYear              = this.minDate ? this.minDate.getFullYear() : 0;
            let maxDateYear          = this.maxDate ? this.maxDate.getFullYear() : 0;
            let minMonth             = this.minDate ? this.minDate.getMonth() : 0;
            let maxMonth             = this.maxDate ? this.maxDate.getMonth() : 0;
            let months               = [];
            let selectedMonthInRange = false;
            for (let value = 0; value < 12; value++)
            {
                if (!(this.selectedYear === minYear && value < minMonth || this.selectedYear === maxDateYear && value > maxMonth))
                {
                    months.push({
                        value,
                        label: monthNames[value],
                        disabled: false,
                    });

                    if (this.selectedMonth === value)
                    {
                        selectedMonthInRange = true;
                    }
                }
            }

            if (!selectedMonthInRange)
            {
                this.selectedMonth = months[0].value;
            }

            let monthStr = ('0' + (this.selectedMonth + 1)).slice(-2);

            // check if next month active
            if (this.maxDate)
            {
                let nextMonth = new Date(this.selectedYear + '-' + monthStr + '-01');
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                this.showNextMonth = nextMonth < this.maxDate;
            }

            // check if previous month active
            if (this.minDate)
            {
                let previousMonth = new Date(this.selectedYear + '-' + monthStr + '-01');
                previousMonth.setDate(previousMonth.getDate() - 1);
                this.showPreviousMonth = previousMonth > this.minDate;
            }

            return months;
        }

        get availableYears()
        {
            let years     = [];
            let startYear = this.selectedYear - 100;
            let endYear   = this.selectedYear + 25;

            if (this.maxDate && endYear > this.maxDate.getFullYear())
            {
                endYear = this.maxDate.getFullYear();
            }
            if (this.minDate && startYear < this.minDate.getFullYear())
            {
                startYear = this.minDate.getFullYear();
            }

            for (let year = startYear; year <= endYear; year++)
            {
                years.push(year);
            }

            return years;
        }

        get availableDays(): AvailableDay[][]
        {
            let firstDayOfWeek = 1;
            let date           = new Date(this.selectedYear, this.selectedMonth);
            let today          = new Date();
            let currentDate    = today.getFullYear() + '-' + today.getMonth() + '-' + today.getDate();

            date.setHours(3);

            // shift until first displayed day equals first day of week
            while (date.getDay() !== firstDayOfWeek)
            {
                date.setDate(date.getDate() - 1);
            }

            let selectedDate  = !this.selectedDate ? null : dateHelper('Y-m-d', this.selectedDate);
            let foundSelected = false;
            let rows          = [];
            let row           = [];
            for (let i = 0; i < 42; i++)
            {
                let year  = date.getFullYear();
                let month = date.getMonth();

                if (i === 35 && month !== this.selectedMonth)
                {
                    break;
                }

                let datetime   = dateHelper('Y-m-d', date) as string;
                let day        = date.getDate();
                let sameMonth  = month === this.selectedMonth;
                let restricted = false;

                // check if this day is restricted
                if (this.minDate && date < this.minDate)
                {
                    restricted = true;
                }
                else if (this.maxDate && date > this.maxDate)
                {
                    restricted = true;
                }
                else if (this.blockedDaysDates.includes(datetime))
                {
                    restricted = true;
                }

                let selected = false;
                if (!foundSelected && selectedDate && selectedDate === datetime)
                {
                    selected      = true;
                    foundSelected = true;
                }

                let active;
                if (this.isWeekPicker)
                {
                    active = dateHelper('Y-m-d', setToMonday(new Date(today.getFullYear() + '-' + (today.getMonth() + 1) + '-' + today.getDate())))
                        === dateHelper('Y-m-d', setToMonday(new Date(this.selectedYear + '-' + (month + 1) + '-' + day)));
                }
                else
                {
                    active = currentDate === this.selectedYear + '-' + month + '-' + day;
                }

                let label = restricted ? this.getBlockedDayReason(datetime) : '';

                const {
                          isHoliday,
                          holidayTitle,
                          blocked,
                      } = this.getHolidayInfo(date);

                if (isHoliday)
                {
                    label      = (label ? label + `<br><br>` : '') + holidayTitle;
                    restricted = restricted || blocked;
                }

                row.push({
                    active: active,
                    weekend: [0, 6].includes(date.getDay()),
                    sameMonth,
                    restricted,
                    selected,
                    day,
                    month,
                    year,
                    label: label,
                    firstDayOfWeek: date.getDay() === firstDayOfWeek,
                    week: '' + dateHelper('W', date) as string,
                    holiday: isHoliday,
                });
                date.setDate(day + 1);

                // push row and start a new
                if (row.length === 7)
                {
                    rows.push(row);
                    row = [];
                }
            }

            return rows;
        }

        get availableHours()
        {
            let rows            = [],
                row             = [],
                isTimeType      = this.type === 'time',
                minHour         = isTimeType && this.minDate ? this.minDate.getHours() : null,
                maxHour         = isTimeType && this.maxDate ? this.maxDate.getHours() : null,
                hourRestriction = false;

            if (isTimeType && (minHour !== null || maxHour !== null))
            {
                hourRestriction = true;
            }

            for (let i = 0; i < 24; i++)
            {
                row.push({
                    hour: i,
                    restricted: hourRestriction && ((minHour !== null && i < minHour) || (maxHour !== null && i > maxHour)),
                });

                if (row.length === 6)
                {
                    rows.push(row);
                    row = [];
                }
            }

            return rows;
        }

        get availableMinutes()
        {
            let rows              = [],
                row               = [],
                isTimeType        = this.type === 'time',
                minHour           = isTimeType && this.minDate ? this.minDate.getHours() : null,
                maxHour           = isTimeType && this.maxDate ? this.maxDate.getHours() : null,
                minMinute         = null,
                maxMinute         = null,
                minuteRestriction = false;

            if (isTimeType)
            {
                if (minHour !== null && this.selectedHour === minHour)
                {
                    minMinute         = this.minDate ? this.minDate.getMinutes() : null;
                    minuteRestriction = true;
                }

                if (maxHour !== null && this.selectedHour === maxHour)
                {
                    maxMinute         = this.maxDate ? this.maxDate.getMinutes() : null;
                    minuteRestriction = true;
                }
            }

            for (let minute = 0; minute < 60; minute += this.minutesSteps)
            {
                row.push({
                    value: minute,
                    minute: ('0' + minute).slice(-2),
                    restricted: minuteRestriction && ((minMinute !== null && minute < minMinute) || (maxMinute !== null && minute > maxMinute)),
                });

                if (row.length === 6)
                {
                    rows.push(row);
                    row = [];
                }
            }
            return rows;
        }

        get blockedDaysDates()
        {
            return Object.keys(this.blockedDays);
        }

        get noHandle()
        {
            return this.disabled || this.readonly;
        }

        created()
        {
            if (this.modelValue || this.defaultNow)
            {
                let selectedDate = this.setDefaultDate();
                this.updateSelectedDate(selectedDate);
            }

            this.$nextTick().then(() =>
            {
                this.initialized = true;

                this.focus && this.focusInput();
            });

            if (this.useHolidays)
            {
                this.fetchHolidays();
            }
        }

        parseCurrentInput()
        {
            let value                   = this.$refs.input.value;
            let newDate: Nullable<Date> = null;

            if (this.isWeekPicker)
            {
                return;
            }

            if (value)
            {
                if ('time' === this.type)
                {
                    value = dateHelper('Y-m-d') + ' ' + value;
                }

                newDate = parseDate(value) as Date;

                if (isNaN(newDate.getTime()))
                {
                    newDate = null;
                }
            }

            this.updateSelectedDate(newDate);
            this.valueChanged = false;
        }

        focusout(event: FocusEvent)
        {
            let target: Nullable<HTMLElement> = event.relatedTarget || document.activeElement;

            // handle clear-button removed due to empty input or focus on e.g. form-error which could be removed on valid input change
            // wait for input change handling
            this.$nextTick(() => {
                // wait for rendering
                window.setTimeout(() => {
                    // check has focus
                    if (document.body.contains(target) || document.activeElement && document.activeElement !== document.body)
                    {
                        return;
                    }

                    // lost focus due to removed element -> refocus inputClear or input
                    if (this.$refs.inputClear)
                    {
                        this.$refs.inputClear.focus();
                    }
                    else
                    {
                        this.focusInput();
                    }
                }, 100);
            });

            if (this.$el.contains(target))
            {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            if (this.valueChanged)
            {
                this.parseCurrentInput();
            }

            this.close(true);
            this.$emit('blur');
        }

        open()
        {
            if (this.noHandle)
            {
                return;
            }

            if (!this.selectedYear)
            {
                this.setDefaultDate();
            }

            this.popover?.open();
        }

        close(parse = false)
        {
            if (parse)
            {
                this.parseCurrentInput();
            }

            this.popover?.close();
        }

        setDefaultDate()
        {
            let selectedDate;
            if (this.modelValue)
            {
                selectedDate = new Date(dateHelper('U', 'time' === this.type ? dateHelper('Y-m-d ') + this.modelValue : this.modelValue) as number * 1000);
            }
            else
            {
                selectedDate = new Date();

                if (!this.defaultNow)
                {
                    selectedDate.setMinutes(0);
                }
            }

            if (this.minDate && selectedDate < this.minDate)
            {
                selectedDate = new Date(this.minDate.getTime());
            }
            else if (this.maxDate && selectedDate > this.maxDate)
            {
                selectedDate = new Date(this.maxDate.getTime());
                selectedDate.setDate(selectedDate.getDate() - 1);
            }

            this.setSelectedDate(selectedDate);

            return selectedDate;
        }

        selectToday()
        {
            let today = new Date();

            if (this.isWeekPicker)
            {
                setToMonday(today);
            }

            this.updateSelectedDate(today);
            this.close();
        }

        selectMinute(minuteInfo: AvailableMinute)
        {
            if (!minuteInfo.restricted)
            {
                this.selectedMinute = minuteInfo.value;

                this.updateTime();
                this.focusInput();
            }
        }

        selectHour(hourInfo: AvailableHour)
        {
            if (!hourInfo.restricted)
            {
                this.selectedHour = hourInfo.hour;

                this.updateTime();
                this.focusInput();
            }
        }

        updateTime()
        {
            this.updateSelectedDate(new Date(this.selectedYear, this.selectedMonth, this.selectedDay, this.selectedHour, this.selectedMinute));
            this.validateValue(this.selectedDate);
        }

        clear(event?: MouseEvent)
        {
            if (this.noHandle)
            {
                return;
            }

            event?.preventDefault();
            this.updateSelectedDate(null);

            this.focusInput();
        }

        focusInput()
        {
            this.$refs.input.focus();
        }

        nextMonth()
        {
            if (!this.showNextMonth)
            {
                return;
            }

            if (this.selectedMonth === 11)
            {
                this.selectedMonth = 0;
                this.selectedYear++;
            }
            else
            {
                this.selectedMonth++;
            }

            this.focusInput();
        }

        previousMonth()
        {
            if (!this.showPreviousMonth)
            {
                return;
            }

            if (this.selectedMonth === 0)
            {
                this.selectedMonth = 11;
                this.selectedYear--;
            }
            else
            {
                this.selectedMonth--;
            }

            this.focusInput();
        }

        updateSelectedDate(date: Nullable<Date>)
        {
            let emptyDate = date === null;
            if (date === null)
            {
                date = new Date();
            }

            if (this.isWeekPicker) {
                if (this.minDate && date < this.minDate)
                {
                    date = setToMonday(this.minDate);
                }
                else if (this.maxDate && date > this.maxDate)
                {
                    date = setToMonday(this.maxDate);
                }
            } else {
                if (this.minDate && date < this.minDate)
                {
                    date = new Date(this.minDate.getTime());
                }
                else if (this.maxDate && date > this.maxDate)
                {
                    date = new Date(this.maxDate.getTime());
                }
            }

            this.selectedDate  = !emptyDate ? dateHelper(getDateFormat('Y-m-d', this.type), date) as string : '';
            this.ignoreWatcher = emptyDate;

            if (emptyDate && this.type === 'time')
            {
                this.resetSelection();
            }
            else
            {
                this.setSelectedDate(date);
            }

            this.$emit('update:modelValue', this.selectedDate);
            this.$emit('change', this.selectedDate);
            this.updateValue();

            if (this.closeOnInput)
            {
                this.close();
            }

            this.$nextTick().then(() =>
            {
                this.ignoreWatcher = false;
            });
        }

        changeDay(dayInfo: AvailableDay)
        {
            if (!dayInfo.restricted)
            {
                this.selectedDay   = dayInfo.day;
                this.selectedMonth = dayInfo.month;
                this.selectedYear  = dayInfo.year;

                this.updateSelectedDate(new Date(this.selectedYear, this.selectedMonth, this.selectedDay, this.selectedHour, this.selectedMinute));
                this.validateValue(this.selectedDate);
                this.focusInput();
            }
            else if (this.isWeekPicker)
            {
                if (this.availableDays.some(days => days.some(day => day === dayInfo) && days.some(day => !day.restricted)))
                {
                    this.selectedDay   = dayInfo.day;
                    this.selectedMonth = dayInfo.month;
                    this.selectedYear  = dayInfo.year;

                    this.updateSelectedDate(new Date(this.selectedYear, this.selectedMonth, this.selectedDay, this.selectedHour, this.selectedMinute));
                    this.validateValue(this.selectedDate);
                    this.focusInput();
                }
            }
        }

        setSelectedDate(date: Date)
        {
            this.selectedYear   = date.getFullYear();
            this.selectedMonth  = date.getMonth();
            this.selectedDay    = date.getDate();
            this.selectedHour   = date.getHours();
            this.selectedMinute = date.getMinutes();
        }

        resetSelection()
        {
            this.selectedYear   = 0;
            this.selectedMonth  = 0;
            this.selectedDay    = 0;
            this.selectedHour   = 0;
            this.selectedMinute = 0;
        }

        getBlockedDayReason(date: string)
        {
            return this.blockedDays[date] ?? '';
        }

        updateValue()
        {
            return this.validateValue(this.selectedDate);
        }

        triggerFocus()
        {
            this.open();
        }

        onPaste(): void
        {
            this.parseCurrentInput();
        }
    };
</script>